import * as React from 'react';

import { useDebounceFn, useRequest } from 'ahooks';
import { Col, Form, Row, Select } from 'antd';

import { RichTextEditor } from 'components';
import { useCatalogAction, useCatalogValue } from 'context';
import { getBrands } from 'services';
import { SelectOption } from 'types';
import {
  findElementByKey,
  generateSelectOptions,
  inputRule,
  normalizeToString,
} from 'utils';

type FieldType = Pick<
  ReturnType<typeof useCatalogValue>,
  'brand' | 'description'
>;

const CatalogBaseInfo = () => {
  const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
    cacheKey: 'brands',
  });

  const { description, ownerType } = useCatalogValue();
  const action = useCatalogAction();

  const { run: debounceUpdateDescription } = useDebounceFn(
    (debouncedDescription: typeof description) => {
      action({
        type: 'UPDATE_CATALOG_DESCRIPTION',
        payload: {
          description: debouncedDescription,
        },
      });
    },
  );

  const { brands = [] } = brandsData ?? {};

  const brandsSelectOptions = generateSelectOptions(
    brands,
    'id',
    'title',
    'id',
  );

  if (!ownerType) return <React.Fragment />;

  return (
    <Row>
      <Col span={24}>
        <Form.Item<FieldType>
          label='برند'
          name='brand'
          rules={[inputRule.required]}
        >
          <Select<SelectOption | undefined>
            allowClear
            labelInValue
            showSearch
            maxTagCount='responsive'
            placeholder='برند کاتالوگ را انتخاب کنید'
            loading={brandsLoading}
            options={brandsSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            onChange={value => {
              const brandId = normalizeToString(value?.value);
              const selectedBrand = findElementByKey(brands, 'id', brandId);

              action({
                type: 'UPDATE_CATALOG_BRAND',
                payload: {
                  brand: selectedBrand,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='توضیحات' name='description'>
          <RichTextEditor
            ownerType={ownerType}
            options={{
              content: description,
              onUpdate: ({ editor }) => {
                debounceUpdateDescription(editor.getHTML());
              },
            }}
            config={{
              placeholder: {
                placeholder: 'توضیحات کاتالوگ را وارد کنید',
              },
            }}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { CatalogBaseInfo };
